import { Injectable } from '@angular/core';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import { LiveEvent, QuestionModel } from 'src/app/api/models';
import {
  BooksService,
  CoursesService,
  ExamsService,
  LiveEventsService,
} from 'src/app/api/services';
import { PanelService } from '../panel.service';
import { Panel, PanelEntityType } from '../panel.types';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, of, throwError } from 'rxjs';
import * as _ from 'lodash';
import { Router } from '@angular/router';
import { UserJourneyService } from '../../pages/onboarding-steps/user-journey.service';
import { LiveEventEditConfigService } from '../../edit/configs/live-event.service';
import { XLSXReader } from '../../common/import-helpers/xlsxReader';
import {
  SentryInfo,
  settingAnswers,
  settingData,
  validateQuestions,
  validateSchema,
} from 'src/app/utility/app.utils';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { SubscriptionLimitService } from '../../common/dialogs/subscription-limit-warning/subscription-limit.service';
import { SubscriptionLimit } from '../../common/dialogs/subscription-limit-warning/subscription-limit-warning.component';
import { QuestionType } from 'src/app/app.types';
import { schemas } from 'src/app/payload-validators';
import { convertToExamAction } from '../../live-event/cms-live-event-select-event.component';
import { WizardImports } from '../../list/list.types';

@Injectable()
export class EventsPanel extends Panel<LiveEvent> {
  isBook = false;

  constructor(
    private courses: CoursesService,
    private books: BooksService,
    private events: LiveEventsService,
    private panelService: PanelService,
    private translator: TranslateService,
    private router: Router,
    private userJourney: UserJourneyService,
    private matSnackBar: MatSnackBar,
    private matDialog: MatDialog,
    private subscriptionLimit: SubscriptionLimitService,
    private exams: ExamsService
  ) {
    super();
    this.initializePanel();
  }

  initializePanel() {
    this.isBook =
      location?.pathname?.split('/')?.[3] === 'book' ||
      this.router.url.indexOf('/book') !== -1;
    super.init({
      addMenuItems: [
        {
          icon: 'add',
          onClick: () => this.panelService.editItem(this),
          text: this.translator.instant('liveEvents.list.create'),
        },
      ],
      wizardImports: getWizardEventImports(
        this.translator,
        this.panelService,
        this,
        this.importFromKahoot.bind(this)
      ),
      imports: [
        {
          title: this.translator.instant(
            'content.event.title.importFromExistingLiveEvent'
          ),
          description: this.translator.instant(
            'content.event.description.importFromExistingLiveEvent'
          ),
          action: {
            icon: 'move_to_inbox',
            click: () => this.panelService.importItems(this),
            text: this.translator.instant(
              'content.event.addMenu.linkLiveEvent'
            ),
          },
        },
        {
          title: this.translator.instant('content.event.title.importFromJSON'),
          description: this.translator.instant(
            'content.event.description.importFromJSON'
          ),
          action: {
            icon: 'code',
            click: () => this.panelService.importItemAsJSON(this),
            text: this.translator.instant(
              'content.event.addMenu.liveEventFromJSON'
            ),
          },
        },
        {
          title: this.translator.instant('liveEvents.list.importfromKahoot'),
          description: this.translator.instant(
            'liveEvents.description.importFromKahoot'
          ),
          action: {
            text: this.translator.instant(
              'liveEvents.liveEventDialogTitles.importFromKahoot'
            ),
            icon: 'description',
            click: () => {
              this.importFromKahoot();
            },
          },
          extra: {
            name: this.translator.instant(
              'liveEvents.liveEventDialogTitles.howItWorks'
            ),
            type: 'link',
            icon: 'keyboard_arrow_right',
            click: () => {
              window.open(
                'https://quizacademy.de/tipps/kahoot-inhalte-importieren/',
                '_blank'
              );
            },
          },
        },
      ],
      childTypes: [PanelEntityType.EventQuestion],
      createItemsFn: (items, courseId) => {
        const sentryInfo = {
          title: `Error while creating Event`,
          file: 'event-panel.ts',
          api: 'createLiveEvent',
          preventLogs: true,
        };
        const { errorMessage, isInvalidSchema } = validateSchema(
          schemas.LiveEvent,
          items[0],
          this.matSnackBar,
          sentryInfo
        );
        if (isInvalidSchema) {
          return of({ error: errorMessage });
        }
        return this.events
          .createLiveEvent({
            body: items[0],
          })
          .pipe(
            switchMap((event) => {
              this.userJourney.set('importedLiveEvent');
              if (courseId) {
                const query = {
                  id: courseId,
                  body: event,
                };
                const op = this.isBook
                  ? this.books.addEventToBook(query)
                  : this.courses.addEventToCourse(query);
                return forkJoin([of(event), op]);
              } else {
                return forkJoin([of(event), of(null)]);
              }
            }),
            map(([event, _]) => [event])
          );
      },
      createEntity: (event, courseId) => {
        return this.events
          .createLiveEvent({
            body: event,
          })
          .pipe(
            switchMap((createdEvent) => {
              if (!courseId) {
                return of(createdEvent);
              }
              const query = {
                id: courseId,
                body: createdEvent,
              };
              const add$ = this.isBook
                ? this.books.addEventToBook(query)
                : this.courses.addEventToCourse(query);
              return forkJoin([of(createdEvent), add$]);
            }),
            map((createdEvent) => createdEvent[0]),
            catchError(() =>
              throwError(
                () =>
                  new Error(
                    this.translator.instant('content.event.liveEventCopyError')
                  )
              )
            )
          );
      },
      editConfigService: LiveEventEditConfigService,
      editConfigServiceParams: (_, courseId) => ({ courseId }),
      editDialogInput: (liveEvent, courseId) => ({ liveEvent, courseId }),
      fetchItem: (id) => this.events.getLiveEventById({ id }),
      fetchItems: (courseId) => {
        return (
          this.isBook
            ? this.books.getEventsInBook({ id: courseId })
            : !_.isNaN(courseId)
            ? this.courses.getEventsInCourse({ id: courseId })
            : of([])
        ).pipe(
          mergeMap((eventsArray: LiveEvent[]) => {
            if (
              !eventsArray ||
              !eventsArray.length ||
              !_.isArray(eventsArray)
            ) {
              this.subscriptionLimit.subscriptionLimit.LIVE_EVENT_COUNT = 0;
              return of([]);
            }
            this.subscriptionLimit.subscriptionLimit.LIVE_EVENT_COUNT =
              eventsArray.length;
            return of(eventsArray);
          })
        );
      },
      fetchChild: (id) => {
        return this.events.getQuestionsForEvent({ id });
      },
      linkChildToEntity: (id, questions: QuestionModel[]) => {
        return this.events.linkQuestionToEvent({ id, body: questions }).pipe(
          map((event) => event),
          catchError(() => {
            return throwError(
              this.translator.instant('content.exam.linkQuestionsError')
            );
          })
        );
      },
      headerIcon: 'live_tv',
      itemOnHoverActions: [
        {
          icon: 'play_circle',
          label: this.translator.instant('content.exam.actionMenu.conduct'),
          onClick: (event) => {
            this.router.navigate([`/cms/live-events/${event.id}`]);
          },
        },
      ],
      itemActions: [
        {
          icon: 'print',
          label: this.translator.instant('content.exam.actionMenu.saveAsPDF'),
          onClick: ({ id }) => {
            window.open(
              `${location.origin}/cms/live-events/print/${id}`,
              '_blank'
            );
          },
        },
        {
          icon: 'copy',
          label: this.translator.instant(
            'content.quiz.actionMenu.duplicateLiveEvent'
          ),
          onClick: (event) => this.panelService.duplicateEntity(this, event),
        },
        {
          icon: 'transform',
          label: this.translator.instant(
            'content.quiz.actionMenu.convertToExam'
          ),
          hidden: () => {
            return this.isBook;
          },
          onClick: (item) => {
            const courseId = +location?.pathname?.split('/')?.[4];
            convertToExamAction(
              item,
              this.events,
              this.exams,
              this.matSnackBar,
              this.translator,
              this.router,
              this.matDialog,
              this.courses,
              courseId
            );
          },
        },
        {
          icon: 'description',
          label: this.translator.instant('content.exam.actionMenu.downloadCSV'),
          onClick: (item) => this.panelService.exportItemAsCSV(this, item.id),
        },
        {
          icon: 'code',
          label: this.translator.instant('content.exam.actionMenu.embed'),
          onClick: (item) => this.panelService.embedItem(this, item),
        },
        {
          icon: 'link_off',
          label: this.isBook
            ? this.translator.instant('content.quiz.actionMenu.removeFromBook')
            : this.translator.instant(
                'content.quiz.actionMenu.removeFromCourse'
              ),
          onClick: (event) => this.panelService.unlinkItems(this, event.id),
        },
      ],
      unlinkItems: (itemIds, courseId) =>
        forkJoin(
          itemIds.map((id) =>
            this.isBook
              ? this.books.removeEventFromBook({
                  id: courseId,
                  event_id: id,
                })
              : this.courses.removeEventFromCourse({
                  id: courseId,
                  event_id: id,
                })
          )
        ),
      itemTextProperty: 'name',
      itemHeaderText: 'liveEvent.table.name',
      parentKey: 'events',
      parentType: PanelEntityType.Course,
      subscriptionLimitType: SubscriptionLimit.LIVE_EVENT_COUNT,
      selectedItemActions: [
        {
          icon: 'link_off',
          label: this.translator.instant('content.question.selectedItem.label'),
          onClick: (selectedItemIds) =>
            this.panelService.unlinkItems(this, selectedItemIds),
        },
      ],
      translations: {
        addItem: this.translator.instant('content.event.tooltip'),
        deleteItemError: this.translator.instant(
          'content.exam.deleteItem.error'
        ),
        deleteItemSuccess: this.translator.instant(
          'content.exam.deleteItem.success'
        ),
        deleteItemText: this.translator.instant('content.exam.deleteItem.text'),
        deleteItemTitle: this.translator.instant(
          'content.exam.deleteItem.title'
        ),
        header: 'Live-Events',
        duplicateEntity: this.translator.instant('content.event.copyLiveEvent'),
        duplicateEntitySuccess: this.translator.instant(
          'content.event.liveEventCopied'
        ),
        linkChild: this.translator.instant('content.event.linkQuestions'),
        unlinkItemConfirmText: this.isBook
          ? this.translator.instant('content.event.unlinkItem.textBook')
          : this.translator.instant('content.event.unlinkItem.text'),
        unlinkItemConfirmTitle: this.isBook
          ? this.translator.instant('content.event.unlinkItem.titleBook')
          : this.translator.instant('content.event.unlinkItem.title'),
        unlinkItemError: this.isBook
          ? this.translator.instant('content.event.unlinkItem.errorBook')
          : this.translator.instant('content.event.unlinkItem.error'),
        unlinkItemSuccess: this.isBook
          ? this.translator.instant('content.event.unlinkItem.successBook')
          : this.translator.instant('content.event.unlinkItem.success'),
      },
      type: PanelEntityType.Event,
    });
  }

  async importFromKahoot() {
    const excelFile = await this.panelService.selectExcelFileAsBinary();
    const file = XLSXReader.excelToArray(excelFile);
    const name = file[0][0];
    if (!name || !name.trim()) {
      this.matSnackBar.open(
        this.translator.instant(
          'liveEvents.list.importFromExcel.provideNameForLiveEvent'
        )
      );
      return;
    }
    let liveEvent;
    const snackbar = this.matSnackBar.open(
      this.translator.instant('liveEvents.list.importFromExcel.createLiveEvent')
    );
    const questions = this.panelService.importQuestionsFromKahoot(excelFile);
    let questionArr = settingData(questions);
    settingAnswers(questionArr);
    questionArr.forEach((question) => {
      if (question['type'] === 'True/False') {
        question['type'] = 1;
        if (question['answers'].length && question['answers'][0]['is_right']) {
          question['is_right'] = question['answers'][0]['is_right'];
        }
        delete question['answers'];
      } else if (question['type'] === 'Multiple Choice') {
        question['type'] = 0;
      } else if (question['type'] === 'Single Choice') {
        question['type'] = 2;
      }
    });
    questionArr = questionArr
      .filter((q) => q.type === QuestionType.TRUE_FALSE || q.answers?.length)
      .map((q) => ({
        ...q,
        is_poll_question:
          q.type === QuestionType.FREETEXT ? true : q.is_poll_question,
      }));
    if (name) {
      const sentryInfo: SentryInfo = {
        title: `Error while importing live event from kahoot`,
        api: 'copyQuestionToEvent',
        preventLogs: true,
        file: 'events-panel.ts -> importFromKahoot()',
      };
      const { isValid } = validateQuestions(
        sentryInfo,
        questionArr,
        this.translator,
        this.matSnackBar
      );
      if (!isValid) {
        return;
      }
      const parentId =
        this.panelService.getParentItemId(PanelEntityType.Event) ?? null;
      liveEvent = await this.events
        .createLiveEvent({
          body: {
            name: name,
          } as LiveEvent,
        })
        .pipe(
          switchMap((event) => {
            this.userJourney.set('importedLiveEvent');
            if (parentId) {
              const query = {
                id: parentId,
                body: event,
              };
              const op = this.isBook
                ? this.books.addEventToBook(query)
                : this.courses.addEventToCourse(query);
              return forkJoin([of(event), op]);
            } else {
              return forkJoin([of(event), of(null)]);
            }
          }),
          map(([event, _]) => [event])
        )
        .toPromise();
      this.matSnackBar.open(
        this.translator.instant(
          'liveEvents.list.importFromExcel.addingQuestionsToLiveEvent'
        )
      );
      this.userJourney.set('importedLiveEvent');
      await this.events
        .copyQuestionToEvent({
          id: liveEvent[0].id,
          body: questionArr,
        })
        .toPromise();
      snackbar.dismiss();
      this.matSnackBar.open(
        this.translator.instant(
          'liveEvents.list.importFromExcel.liveEventCreated'
        )
      );
      this.matDialog.closeAll();
      // router.navigate(['cms', 'live-events', liveEvent.id]);
    }
  }
}

export const getWizardEventImports = (
  translator: TranslateService,
  panelService: PanelService,
  panel: Panel<LiveEvent>,
  importFromKahoot: () => void
): WizardImports => {
  return {
    createWithAI: {
      text: translator.instant('content.wizardActions.title.createWithAI'),
      icon: 'auto_awesome',
      description: translator.instant(
        'content.wizardActions.description.createLiveEventWithAI'
      ),
      tag: translator.instant('content.wizardActions.tag.createWithAI'),
      click: () => {
        panelService.createWithAI(panel);
      },
    },
    createButton: {
      text: translator.instant('content.wizardActions.title.createManually'),
      icon: 'edit',
      description: translator.instant(
        'content.wizardActions.description.createManually'
      ),
      click: () => panelService.editItem(panel),
    },
    importButtons: [
      {
        text: translator.instant(
          'content.wizardActions.title.useExistingLiveEvent'
        ),
        description: translator.instant(
          'content.wizardActions.description.useExistingLiveEvent'
        ),
        icon: 'file_copy',
        click: () => panelService.importItems(panel),
      },
      {
        text: translator.instant(
          'content.wizardActions.title.importFromKahoot'
        ),
        description: translator.instant(
          'content.wizardActions.description.importFromKahoot'
        ),
        icon: 'save_alt',
        click: () => importFromKahoot(),
      },
    ],
  };
};
